import { getData } from '../utils/ajax-utils';

declare const script_var: { ajax_url: string };

export function initBaseDocumentaireSearchAutocomplete() : void {
    document.querySelectorAll<HTMLElement>('.js-base-documentaire-search-form').forEach($container => {
        new BaseDocumentaireSearchAutocomplete($container);
    });
}

class BaseDocumentaireSearchAutocomplete {
    $container: HTMLElement;
    $input: HTMLInputElement;
    $autocompleteContainer: HTMLUListElement;
    oldValue: string = '';
    timeoutCallback: ReturnType<typeof setTimeout> | null = null;
    resultsCache: Record<string, string[]> = {};
    abortController: AbortController | null = null;

    constructor ($container: HTMLElement) {
        this.$container = $container;
        this.$input = this.$container.querySelector('.js-base-documentaire-search-form-input') as HTMLInputElement;
        this.$autocompleteContainer = this.$container.querySelector('.js-base-documentaire-search-form-autocomplete') as HTMLUListElement;

        if (!this.$input || !this.$autocompleteContainer) {
            return;
        }

        this.oldValue = this.$input.value;

        this.initAutocompletionDisplayEvents();

        this.$input.addEventListener('input', this.inputHandler.bind(this));
    }

    /**
     * Initialise les événements permettant d'afficher/masquer l'autocompletion
     * 
     * @return {void}
     */
    initAutocompletionDisplayEvents(): void {
        // Affiche l'autocompletion lorsque le focus entre dans container
        this.$container.addEventListener('focusin', () => {
            if (this.$autocompleteContainer.children.length) {
                this.showAutocompletion();
            }
        });

        // Masque l'autocompletion lorsque le focus sort du container
        this.$container.addEventListener('focusout', () => {
            // On attend 50ms pour laisser le temps au focus de bouger
            setTimeout(() => {
                const $focusedElement = document.activeElement;

                // Si le nouvel élément focus n'est pas le body et n'est pas dans le container
                if (!this.$autocompleteContainer.classList.contains('hidden') &&
                    $focusedElement &&
                    !this.$container.contains($focusedElement) &&
                    $focusedElement !== document.body
                ) {
                    this.hideAutocompletion();
                }
            }, 50);
        });

        // Masque l'autocompletion lorsqu'on clique en dehors du container
        document.addEventListener('click', evt => {
            const $clickedElement = evt.target as HTMLElement;

            // Si l'élément cliqué n'est pas dans le container
            if (!this.$autocompleteContainer.classList.contains('hidden') &&
                $clickedElement &&
                !this.$container.contains($clickedElement)
            ) {
                this.hideAutocompletion();
            }
        });

        // Masque l'autocompletion lorsqu'on appuie sur la touche ECHAP
        document.addEventListener('keyup', evt => {
            if (!this.$autocompleteContainer.classList.contains('hidden') && evt.key === 'Escape') {
                this.hideAutocompletion();
            }
        });
    }

    /**
     * Affiche l'autocompletion
     * 
     * @return {void}
     */
    showAutocompletion(): void {
        this.$autocompleteContainer.classList.remove('hidden');
    }

    /**
     * Toggle l'autocompletion
     * 
     * @return {void}
     */
    toggleAutocompletion(force?: boolean): void {
        this.$autocompleteContainer.classList.toggle('hidden', force);
    }

    /**
     * Masque l'autocompletion
     * 
     * @return {void}
     */
    hideAutocompletion(): void {
        this.$autocompleteContainer.classList.add('hidden');
    }

    /**
     * Appelé lorsqu'on tape quelque chose dans l'input
     * 
     * @return {void}
     */
    inputHandler(): void {
        if (this.$input.value === this.oldValue) {
            return;
        }

        this.$autocompleteContainer.innerHTML = '';

        this.oldValue = this.$input.value;
        this.hideAutocompletion();

        // Annulation du lancement différé de la recherche précédente
        const timeoutCallback = this.timeoutCallback;
        if (timeoutCallback) {
            clearTimeout(timeoutCallback);
        }

        // Annulation requête précédente si elle n'est pas terminée
        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();

        if (this.$input.value.length < 3) {
            // On retire le loading
            this.$container.classList.remove('loading');

            return;
        }

        if (typeof this.resultsCache[this.$input.value] !== 'undefined') {
            // Affichage immédiat des résultats s'ils sont stockés dans le cache
            const results = this.resultsCache[this.$input.value];
            this.displayResults(results);

            // On retire le loading
            this.$container.classList.remove('loading');
        } else {
            // Affichage du loading
            this.$container.classList.add('loading');

            // On attend 5 secondes avant de lancer la recherche
            // (pour laisser l'utilisateur continuer de taper un mot s'il le souhaite)
            this.timeoutCallback = setTimeout(() => {
                this.getResults(this.$input.value).then(results => {
                    this.displayResults(results);

                    // Sauvegarde des résultats en cache
                    this.resultsCache[this.$input.value] = results;
                }).catch(() => {}).finally(() => {
                    // On retire le loading et supprime le AbortController
                    this.$container.classList.remove('loading');
                    this.abortController = null;
                });
            }, 500);
        }
    }

    /**
     * Insert les résultats dans le DOM
     * 
     * @param {string[]} results 
     * 
     * @return {void}
     */
    displayResults(results: string[]): void {
        results.forEach(documentTitle => {
            // Création du bouton (pour avoir un tabindex)
            const $button = document.createElement('button');
            $button.innerText = documentTitle;

            // Création du LI
            const $li = document.createElement('li');
            $li.append($button);

            // Ajout du onClick sur le bouton
            this.initEventOnResultItem($button, documentTitle);

            this.$autocompleteContainer.append($li);
        });

        // Affichage de l'autocompletion
        this.showAutocompletion();
    }

    /**
     * Soumission du formulaire avec le contenu du résultat au clique sur celui-ci
     * 
     * @param {HTMLButtonElement} $button
     * @param {string} result
     * 
     * @return {void}
     */
    initEventOnResultItem($button: HTMLButtonElement, result: string): void {
        $button.addEventListener('click', () => {
            this.$input.value = result;
            this.$input.closest('form')?.submit();
        });
    }

    /**
     * Récupère les résultats de recherche via AJAX
     * 
     * @param {string} search
     * 
     * @return {Promise<string[]>}
     */
    async getResults(search: string): Promise<string[]> {
        return await getData(script_var.ajax_url, {
            action: 'get_base_documentaire_search_autocompletion',
            search: search,
        }, this.abortController?.signal).then(response => {
            return response.data;
        });
    }
}
